"use strict";
/**
 * Romanian identifier checksum validators — CUI (fiscal code), IBAN, CNP
 * (personal numeric code). Pure functions, no I/O, so they can run
 * client-side (future frontend) and server-side (zod `.refine()` in
 * `schemas/*.ts`) from the same implementation.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.isValidCui = isValidCui;
exports.isValidIban = isValidIban;
exports.isValidCnp = isValidCnp;
// CUI (Cod Unic de Înregistrare): strip an optional leading "RO" and
// whitespace, keep 2-10 digits where the last digit is a control digit.
// The remaining digits (the "body") are right-aligned against the 9-digit
// key below — i.e. the body's last digit pairs with the key's last digit,
// its second-to-last with the key's second-to-last, and so on.
const CUI_KEY = [7, 5, 3, 2, 1, 7, 5, 3, 2];
function isValidCui(input) {
    if (typeof input !== 'string')
        return false;
    const cleaned = input.trim().toUpperCase().replace(/^RO/, '');
    if (!/^\d{2,10}$/.test(cleaned))
        return false;
    const digits = cleaned.split('').map(Number);
    const control = digits[digits.length - 1];
    const body = digits.slice(0, -1);
    const offset = CUI_KEY.length - body.length;
    const sum = body.reduce((acc, digit, i) => acc + digit * CUI_KEY[offset + i], 0);
    let computed = (sum * 10) % 11;
    if (computed === 10)
        computed = 0;
    return computed === control;
}
// IBAN: general ISO 7064 MOD 97-10 check — move the first 4 characters
// (country code + 2 check digits) to the end, convert letters to numbers
// (A=10 ... Z=35), and verify the resulting numeral mod 97 === 1. Romanian
// IBANs are additionally checked for the fixed length (24) implied by the
// "RO" + 2 check digits + 4-letter bank code + 16-digit account format.
function isValidIban(input) {
    if (typeof input !== 'string')
        return false;
    const cleaned = input.replace(/\s+/g, '').toUpperCase();
    if (!/^[A-Z]{2}\d{2}[A-Z0-9]{4,30}$/.test(cleaned))
        return false;
    if (cleaned.startsWith('RO') && cleaned.length !== 24)
        return false;
    const rearranged = cleaned.slice(4) + cleaned.slice(0, 4);
    const numeric = rearranged.replace(/[A-Z]/g, (letter) => String(letter.charCodeAt(0) - 55));
    try {
        return BigInt(numeric) % 97n === 1n;
    }
    catch {
        return false;
    }
}
// CNP (Cod Numeric Personal): 13 digits, the last one is a control digit
// computed as the weighted sum of the first 12 digits (weights below) mod
// 11, with the special case that a remainder of 10 maps to a control digit
// of 1 (not 0, unlike CUI).
const CNP_KEY = [2, 7, 9, 1, 4, 6, 3, 5, 8, 2, 7, 9];
function isValidCnp(input) {
    if (typeof input !== 'string')
        return false;
    const cleaned = input.trim();
    if (!/^\d{13}$/.test(cleaned))
        return false;
    const digits = cleaned.split('').map(Number);
    const control = digits[12];
    const sum = CNP_KEY.reduce((acc, weight, i) => acc + weight * digits[i], 0);
    let computed = sum % 11;
    if (computed === 10)
        computed = 1;
    return computed === control;
}
